import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { WhatsNewAnnouncement, WhatsNewStep } from './whats-new.types';
import { WhatsNewCta } from './whats-new.cta';
import { AutoscalingVisualComponent } from './visuals/autoscaling-visual.component';
import { McpVisualComponent } from './visuals/mcp-visual.component';

/**
 * What the dialog is handed. The calls to action arrive already resolved — see
 * {@link WhatsNewCta} for why that is not done on click.
 */
export interface WhatsNewDialogData {
  announcement: WhatsNewAnnouncement;
  /** `null` when this build has nowhere useful to send the reader. */
  primary: WhatsNewCta | null;
  secondary: WhatsNewCta | null;
}

@Component({
  selector: 'whats-new-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    FormsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    AutoscalingVisualComponent,
    McpVisualComponent,
  ],
  templateUrl: './whats-new-dialog.component.html',
  styleUrl: './whats-new-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WhatsNewDialogComponent {
  stepIndex = 0;

  /**
   * Ticked by default: doing nothing means "understood, do not bring this up
   * again". Unticking is the deliberate act of asking for it once more.
   */
  dismissForGood = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WhatsNewDialogData,
    private _dialogRef: MatDialogRef<WhatsNewDialogComponent>,
    private _router: Router,
  ) {}

  get announcement(): WhatsNewAnnouncement {
    return this.data.announcement;
  }

  get step(): WhatsNewStep {
    return this.announcement.steps[this.stepIndex];
  }

  get isFirstStep(): boolean {
    return this.stepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.stepIndex === this.announcement.steps.length - 1;
  }

  next(): void {
    if (!this.isLastStep) this.stepIndex++;
  }

  back(): void {
    if (!this.isFirstStep) this.stepIndex--;
  }

  goToStep(index: number): void {
    this.stepIndex = index;
  }

  close(): void {
    this._dialogRef.close(this.dismissForGood);
  }

  /**
   * Follow one of the announcement's calls to action.
   *
   * Where it leads was decided before this dialog opened, so that the button's
   * label could be decided with it.
   */
  runCta(cta: WhatsNewCta): void {
    if (cta.kind === 'external') {
      // Opens in a new tab; the dialog stays put so the reader can still use
      // the primary action afterwards.
      window.open(cta.url, '_blank', 'noopener');
      return;
    }

    this.close();
    void this._router.navigate(cta.commands);
  }
}
